import React from "react";
import { Dialog, Theme, createStyles, withStyles, WithStyles } from "@material-ui/core";
import DialogToolbar from "./DialogToolbar";
import { DialogMaxWidth, getDialogBackdropProps, getDialogLayout } from "../../consts/dialog";
import DialogPresentationContext from "./DialogPresentationContext";

type MaxWidthType = DialogMaxWidth;

let showAuxiliaryDialog = (
  _content: React.ReactNode,
  _maxWidth?: MaxWidthType,
  _fullScreen?: boolean,
  _backDropClickToClose?: boolean,
  _toolbar?: boolean,
  _title?: string,
  _widescreen?: boolean,
  _subtitle?: string,
  _showTrophy?: boolean,
): void => {};

let hideAuxiliaryDialog = (): void => {};

let updateAuxiliaryDialogMeta = (
  _title?: string,
  _subtitle?: string,
  _showTrophy?: boolean,
  _statusLabel?: string,
): void => {};

interface State {
  content: React.ReactNode | null;
  open: boolean;
  title: string;
  subtitle: string;
  statusLabel: string;
  showTrophy: boolean;
  fullScreen: boolean;
  fullWidth: boolean;
  maxWidth: MaxWidthType;
  paperStyle?: React.CSSProperties;
  backDropClickToClose: boolean;
  toolbar: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    dialogPaper: {
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    contentArea: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
    },
  });

interface Props extends WithStyles<typeof styles> {}

class AuxiliaryDialog extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      title: "",
      subtitle: "",
      statusLabel: "",
      showTrophy: false,
      content: null,
      maxWidth: "md",
      open: false,
      fullScreen: false,
      fullWidth: true,
      paperStyle: undefined,
      backDropClickToClose: false,
      toolbar: true,
    };
  }

  componentDidMount() {
    showAuxiliaryDialog = this.showDialog;
    hideAuxiliaryDialog = this.closeDialog;
    updateAuxiliaryDialogMeta = this.updateDialogMeta;
  }

  componentWillUnmount() {
    showAuxiliaryDialog = () => {};
    hideAuxiliaryDialog = () => {};
    updateAuxiliaryDialogMeta = () => {};
  }

  updateDialogMeta = (title = "", subtitle = "", showTrophy = false, statusLabel = ""): void => {
    if (!this.state.open) return;
    this.setState({ title, subtitle, showTrophy, statusLabel });
  };

  showDialog = (
    content: React.ReactNode,
    maxWidth: MaxWidthType = "md",
    fullScreen = false,
    backDropClickToClose = false,
    toolbar = true,
    title = "",
    widescreen = false,
    subtitle = "",
    showTrophy = false,
  ): void => {
    const layout = getDialogLayout({ maxWidth, fullScreen, widescreen });

    this.setState({
      open: true,
      content,
      fullScreen: layout.fullScreen,
      fullWidth: layout.fullWidth,
      maxWidth: layout.maxWidth,
      paperStyle: layout.paperStyle,
      backDropClickToClose,
      toolbar,
      title,
      subtitle: subtitle || "",
      showTrophy,
    });
  };

  closeDialog = (): void => {
    this.setState({ open: false, content: null, title: "", subtitle: "", statusLabel: "", showTrophy: false });
  };

  handleBackdropClick = (): void => {
    if (this.state.backDropClickToClose) {
      this.closeDialog();
    }
  };

  render() {
    const { classes } = this.props;
    const {
      maxWidth,
      open,
      content,
      toolbar,
      title,
      subtitle,
      statusLabel,
      showTrophy,
      fullScreen,
      fullWidth,
      paperStyle,
    } = this.state;

    return (
      <Dialog
        open={open}
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper, style: paperStyle }}
        BackdropProps={getDialogBackdropProps()}
        disableEnforceFocus
        onClose={this.handleBackdropClick}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        fullScreen={fullScreen}
        aria-labelledby={toolbar && title ? "aux-dialog-title" : undefined}
      >
        <DialogPresentationContext.Provider value={{ isFullscreen: fullScreen }}>
          {toolbar ? (
            <DialogToolbar
              title={title}
              subtitle={subtitle}
              statusLabel={statusLabel}
              showTrophy={showTrophy}
              titleId="aux-dialog-title"
              onClick={this.closeDialog}
            />
          ) : null}
          <div className={classes.contentArea}>{content}</div>
        </DialogPresentationContext.Provider>
      </Dialog>
    );
  }
}

export { showAuxiliaryDialog, hideAuxiliaryDialog, updateAuxiliaryDialogMeta };
export default withStyles(styles)(AuxiliaryDialog);
